import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const sessionId = searchParams.get('session_id');
  const planId = searchParams.get('plan');
  const [status, setStatus] = useState('loading'); // loading | paid | failed
  const [attempts, setAttempts] = useState(0);
  const pollingRef = useRef(null);
  const processedRef = useRef(false);

  const pollStatus = async () => {
    if (processedRef.current) return;
    if (!sessionId) { setStatus('failed'); return; }
    try {
      const res = await authFetch(`${BACKEND}/api/payments/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === 'paid') {
        processedRef.current = true;
        clearInterval(pollingRef.current);
        await checkAuth(); // Refresh user to get new subscription plan
        setStatus('paid');
      } else if (attempts >= 8) {
        clearInterval(pollingRef.current);
        setStatus('failed');
      }
    } catch {
      if (attempts >= 8) {
        clearInterval(pollingRef.current);
        setStatus('failed');
      }
    }
    setAttempts(a => a + 1);
  };

  useEffect(() => {
    pollStatus();
    pollingRef.current = setInterval(pollStatus, 2000);
    return () => clearInterval(pollingRef.current);
  }, []);

  const PLAN_NAMES = { recon: 'RECON', intelligence: 'INTELLIGENCE' };
  const planName = PLAN_NAMES[planId] || 'your plan';

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 border-2 border-foreground border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2">Processing payment...</h1>
            <p className="text-muted-foreground text-sm">Please wait, we're confirming your subscription.</p>
          </>
        )}

        {status === 'paid' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2">Welcome to {planName}!</h1>
            <p className="text-muted-foreground mb-8">Your subscription is now active. Let's find your next star hire.</p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/gallery')}
                className="w-full rounded-none bg-foreground text-background hover:bg-foreground/80">
                Browse Talent Gallery
              </Button>
              <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full rounded-none">
                Go to Dashboard
              </Button>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2">Payment not confirmed</h1>
            <p className="text-muted-foreground mb-8">We couldn't confirm your payment. If you were charged, please contact support.</p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/pricing')}
                className="w-full rounded-none bg-foreground text-background hover:bg-foreground/80">
                Back to Pricing
              </Button>
              <Button onClick={() => navigate('/contact')} variant="outline" className="w-full rounded-none">
                Contact Support
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
